import React, { useState } from "react";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import { Alert } from "@components/Alert/Alert";
import Button from "@components/Button/Button";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { useCachedCargoOperationDetail } from "../hooks/useCargoOperationDetail";
import { useConfirmReadiness } from "../hooks/useConfirmReadiness";
import { useIsCargoOpsShipper } from "../hooks/useIsCargoOpsShipper";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.readiness`;
const PENDING_SHIPPER_READINESS_STATUS = "PENDING_SHIPPER_READINESS";
const DIALOG_TEST_ID = "drybulk-cargo-operation-readiness-dialog";

interface ShipperReadinessActionProps {
  operationId: string;
  /** The host pauses its detail polling while the confirmation dialog is open. */
  onDialogOpenChange?: (open: boolean) => void;
}

/**
 * DBCO-0040 (R-17) - the Shipper's *Submit Readiness* on an operation waiting for its readiness, always behind the
 * confirmation dialog. The Shipper's own row is the one of the caller's company; nothing about other Shippers is read. The
 * dialog stays mounted while open even if a re-read makes the button ineligible, so a refusal message is never lost. The
 * server checks again on the call and its message is shown when it refuses.
 */
const ShipperReadinessAction: React.FC<ShipperReadinessActionProps> = ({
  operationId,
  onDialogOpenChange,
}) => {
  const { t } = useTranslation();
  const detail = useCachedCargoOperationDetail(operationId).data;
  const isShipper = useIsCargoOpsShipper();
  const companyId = useAuthStore((state) => state.user?.companyId);
  const readiness = useConfirmReadiness(operationId);
  const [open, setOpen] = useState(false);

  const ownRow = detail?.shippers?.find((row) => row.shipperCompanyCode === companyId);
  const eligible =
    isShipper &&
    detail?.status === PENDING_SHIPPER_READINESS_STATUS &&
    ownRow?.readiness === "PENDING_READINESS";

  const setDialogOpen = (next: boolean) => {
    setOpen(next);
    readiness.clearDialogError();
    onDialogOpenChange?.(next);
  };

  const confirm = async () => {
    if (!ownRow?.shipperCompanyCode) {
      return;
    }
    if (await readiness.confirm(ownRow.shipperCompanyCode)) {
      setDialogOpen(false);
    }
  };

  if (!eligible && !open) {
    return null;
  }

  return (
    <>
      {eligible && (
        <Button
          variant="contained"
          startIcon={<ContentPasteGoIcon />}
          onClick={() => setDialogOpen(true)}
          testId="drybulk-cargo-operation-submit-readiness"
          label={<span className="normal-case font-medium">{t(`${L}.submit`)}</span>}
        />
      )}
      <GenericDialog
        open={open}
        testId={DIALOG_TEST_ID}
        title={t(`${L}.dialog.title`)}
        content={t(`${L}.dialog.message`)}
        elementContent={
          <>
            <span style={{ whiteSpace: "pre-line" }}>{t(`${L}.dialog.message`)}</span>
            {readiness.dialogError && (
              <div data-testid={`${DIALOG_TEST_ID}-error`} style={{ marginTop: 16 }}>
                <Alert type="error">{readiness.dialogError}</Alert>
              </div>
            )}
          </>
        }
        cancelButtonText={t(`${L}.dialog.cancel`)}
        confirmButtonText={t(`${L}.dialog.confirm`)}
        handleCloseDialog={() => setDialogOpen(false)}
        confirmAction={() => void confirm()}
        isSubmitting={readiness.isConfirming}
      />
    </>
  );
};

export default ShipperReadinessAction;
